/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { useMemo } from 'react';
import { zip } from 'lodash';
import { css, GenericDataType, styled } from '@aibi-ui/core';
import {
  CopyToClipboardButton,
  FilterInput,
} from 'src/explore/components/DataTableControl';
import { applyFormattingToTabularData } from 'src/utils/common';
import { getTimeColumns } from 'src/explore/components/DataTableControl/utils';
import RowCountLabel from 'src/explore/components/RowCountLabel';
import { TableControlsProps } from '../types';

export const TableControlsWrapper = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;

    .on-formatted-copy {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 22px;
      height: 22px;
      border-radius: 3px;
      &:hover {
        background: #eef3ff;
        svg {
          color: #5687ff;
        }
      }
      svg {
        width: 14px;
        height: 14px;
      }
    }
    span {
      flex-shrink: 0;
    }
  `}
`;

export const TableControls = ({
  data,
  datasourceId,
  onInputChange,
  columnNames,
  columnTypes,
  rowcount,
  isLoading,
  canDownload,
}: TableControlsProps) => {
  const originalTimeColumns = getTimeColumns(datasourceId);
  const formattedTimeColumns = zip<string, GenericDataType>(
    columnNames,
    columnTypes,
  )
    .filter(
      ([name, type]) =>
        type === GenericDataType.Temporal &&
        name &&
        !originalTimeColumns.includes(name),
    )
    .map(([colname]) => colname);
  const formattedData = useMemo(
    () => applyFormattingToTabularData(data, formattedTimeColumns),
    [data, formattedTimeColumns],
  );
  return (
    <TableControlsWrapper>
      <FilterInput onChangeHandler={onInputChange} shouldFocus />
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <div className="styled-rowcount">
          <RowCountLabel rowcount={rowcount} loading={isLoading} />
        </div>
        {canDownload && (
          <div className="on-formatted-copy">
            <CopyToClipboardButton data={formattedData} columns={columnNames} />
          </div>
        )}
      </div>
    </TableControlsWrapper>
  );
};
